import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useT } from "@/i18n/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Upload, Route, Loader2, FileSpreadsheet } from "lucide-react";
import { format, parseISO } from "date-fns";
import { ptBR, enUS, es } from "date-fns/locale";
import LinkPreview from "@/components/LinkPreview";

const DATE_LOCALES = { pt: ptBR, en: enUS, es };

const empty = {
  toll_datetime: new Date().toISOString().slice(0, 16),
  amount: 0,
  location: "",
  plate: "",
  car_id: "",
  ezpass_tag: "",
  notes: "",
  auto_charge_customer: true,
};

export default function Pedagios() {
  const { t, lang, fmtCurrency } = useT();
  const dateLocale = DATE_LOCALES[lang] || enUS;
  const [items, setItems] = useState([]);
  const [cars, setCars] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    const [tk, cs] = await Promise.all([api.get("/tolls"), api.get("/cars")]);
    setItems(tk.data || []);
    setCars(cs.data || []);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.car_id) {
      toast.error(t("tolls.carRequired"));
      return;
    }
    try {
      const car = cars.find((c) => c.id === form.car_id);
      const payload = {
        ...form,
        amount: Number(form.amount),
        toll_datetime: new Date(form.toll_datetime).toISOString(),
        car_id: form.car_id,
        plate: car?.plate || null,
        ezpass_tag: form.ezpass_tag?.trim() || car?.ezpass_tag || null,
      };
      const r = await api.post("/tolls", payload);
      toast.success(r.data.auto_linked ? t("tolls.createdAutoLinked") : t("tolls.createdNoActiveRental"));
      setOpen(false);
      setForm(empty);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t("tolls.confirmDelete"))) return;
    try {
      await api.delete(`/tolls/${id}`);
      toast.success(t("tolls.deleted"));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const importCSV = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("auto_charge", "true");
      const r = await api.post("/tolls/import-csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("tolls.importedN", { n: r.data.created, linked: r.data.auto_linked }));
      if ((r.data.errors || []).length) {
        console.warn("Toll CSV errors:", r.data.errors);
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const total = items.reduce((a, x) => a + Number(x.amount || 0), 0);

  return (
    <div className="space-y-6" data-testid="pedagios-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="overline">{t("tolls.pageTitle", { n: items.length, total: fmtCurrency(total) })}</div>
          <p className="text-sm text-zinc-500 mt-1">{t("tolls.pageSubtitle")}</p>
        </div>
        <div className="flex gap-2">
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(e) => importCSV(e.target.files?.[0])}
            data-testid="toll-csv-input"
          />
          <Button
            variant="outline"
            className="rounded-none"
            onClick={() => fileRef.current?.click()}
            disabled={importing}
            data-testid="import-csv-button"
          >
            {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            {t("tolls.importCsv")}
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => { setForm(empty); setOpen(true); }} className="rounded-none bg-[#F5A623] hover:bg-[#D97706]" data-testid="add-toll-button">
                <Plus className="w-4 h-4 mr-2" /> {t("tolls.newToll")}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-xl rounded-none">
              <DialogHeader>
                <DialogTitle className="font-display tracking-tight">{t("tolls.newToll")}</DialogTitle>
              </DialogHeader>
              <form onSubmit={submit} className="grid grid-cols-2 gap-4">
                <Field label={t("rentals.vehicle")} full>
                  <Select value={form.car_id} onValueChange={(v) => setForm({ ...form, car_id: v })} required>
                    <SelectTrigger className="rounded-none" data-testid="toll-car-select">
                      <SelectValue placeholder={t("tolls.selectCarRequired")} />
                    </SelectTrigger>
                    <SelectContent>
                      {cars.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.model} — {c.plate}{c.ezpass_tag ? ` · EZ: ${c.ezpass_tag}` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label={t("tolls.datetime")}>
                  <Input type="datetime-local" className="rounded-none" required value={form.toll_datetime}
                    onChange={(e) => setForm({ ...form, toll_datetime: e.target.value })} data-testid="toll-datetime-input" />
                </Field>
                <Field label={t("tolls.amount")}>
                  <Input type="number" step="0.01" className="rounded-none" required value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="toll-amount-input" />
                </Field>
                <LinkPreview carId={form.car_id} datetimeIso={form.toll_datetime} />
                <Field label={t("tolls.location")} full>
                  <Input className="rounded-none" value={form.location}
                    onChange={(e) => setForm({ ...form, location: e.target.value })}
                    placeholder="e.g. Mass Pike Allston Plaza" />
                </Field>
                <div className="col-span-2 flex items-center gap-2 bg-orange-50 border border-[#F5A623]/30 px-3 py-2">
                  <input type="checkbox" id="toll-auto-charge" checked={form.auto_charge_customer}
                    onChange={(e) => setForm({ ...form, auto_charge_customer: e.target.checked })} />
                  <label htmlFor="toll-auto-charge" className="text-xs">{t("tolls.autoChargeHint")}</label>
                </div>
                <DialogFooter className="col-span-2">
                  <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-none">{t("common.cancel")}</Button>
                  <Button type="submit" className="rounded-none bg-[#F5A623] hover:bg-[#D97706]" data-testid="save-toll-button">{t("common.save")}</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 px-4 py-3 text-xs text-blue-900 flex items-start gap-2">
        <FileSpreadsheet className="w-4 h-4 shrink-0 mt-0.5" />
        <div>
          <div className="font-semibold mb-0.5">{t("tolls.csvHintTitle")}</div>
          <div>{t("tolls.csvHintBody")}</div>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="border border-zinc-200 p-16 text-center">
          <Route className="w-12 h-12 mx-auto text-zinc-300 mb-4" />
          <div className="overline mb-2">{t("tolls.emptyTitle")}</div>
          <p className="text-sm text-zinc-500">{t("tolls.emptySub")}</p>
        </div>
      ) : (
        <div className="border border-zinc-200 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr className="text-left">
                <Th>{t("common.date")}</Th>
                <Th>{t("tolls.location")}</Th>
                <Th>{t("rentals.vehicle")}</Th>
                <Th>EZPass</Th>
                <Th>{t("rentals.customer")}</Th>
                <Th>{t("tolls.amount")}</Th>
                <Th>{t("common.status")}</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody>
              {items.map((tl) => (
                <tr key={tl.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                  <Td className="font-mono text-xs">{tl.toll_datetime ? format(parseISO(tl.toll_datetime), "dd MMM yy HH:mm", { locale: dateLocale }) : "—"}</Td>
                  <Td>{tl.location || <span className="text-zinc-400 italic">—</span>}</Td>
                  <Td>{tl.car ? <><span className="font-medium">{tl.car.model}</span> <span className="font-mono text-xs text-zinc-500">{tl.car.plate}</span></> : <span className="text-zinc-400 text-xs italic">{tl.plate || "—"}</span>}</Td>
                  <Td className="font-mono text-xs text-zinc-700">{tl.ezpass_tag || tl.car?.ezpass_tag || <span className="text-zinc-300">—</span>}</Td>
                  <Td>{tl.customer?.name || <span className="text-zinc-400 text-xs italic">—</span>}</Td>
                  <Td className="font-mono font-semibold">{fmtCurrency(tl.amount)}</Td>
                  <Td>
                    {tl.auto_linked ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-mono border bg-green-100 text-green-800 border-green-300">
                        {t("tolls.linked")}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-mono border bg-zinc-100 text-zinc-600 border-zinc-300">
                        {t("tolls.unlinked")}
                      </span>
                    )}
                  </Td>
                  <Td>
                    <button onClick={() => remove(tl.id)}
                      className="p-1.5 border border-zinc-200 hover:border-red-400 hover:text-red-600 ml-auto block">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const Th = ({ children }) => <th className="px-4 py-3 overline">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;
const Field = ({ label, children, full }) => (
  <div className={`space-y-1.5 ${full ? "col-span-2" : ""}`}>
    <label className="overline">{label}</label>
    {children}
  </div>
);
